import React, { useEffect, useState } from 'react';
import styled from "styled-components";
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { HorizontalSpacer } from '@shared/ui/atoms/VerticalSpacer';
import { BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { Switch } from '@shared/ui/atoms/Switch';
import { useTranslation } from "react-i18next";
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { SpaceBetweenRow } from "../../StyleComponents";
import { HospitalityMenuItem } from "./HospitalityMenuItem";
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { useDispatch, useSelector } from "react-redux";
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { DialogIdentifiers, openDialogWithPayload } from "@shared/store/dialog.duck";
import {
  enableHospitalityForOffice,
  loadHospitalityInfo,
  selectAllOrderItems,
  selectIsLoading,
  selectUsersToNotify,
  updateImportantNoteVisibility,
  updateUsersToNotify
} from "../data/hospitality-management.duck";
import { UserSelectType } from '@shared/dialogs/user-search-dialog/UserSearchDialog';
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { BasicUserInfo } from '@shared/models/user.models';

export function HospitalityPanel(props: Props) {
  const {office} = props;
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const isLoading = useSelector(selectIsLoading);
  const orderItems = useSelector(selectAllOrderItems);
  const usersToNotify = useSelector(selectUsersToNotify);
  const [hospitalityEnabled, setHospitalityEnabled] = useState(false);
  const [importantNoteVisible, setImportantNoteVisible] = useState(false);

  useEffect(() => {
    dispatch(loadHospitalityInfo({office: office}))
  }, [dispatch, office]);

  useEffect(() => {
    setImportantNoteVisible(!!office.hospitalImportantNoteVisible)
    setHospitalityEnabled(!!office.hospitalityEnabled);
  }, [office.hospitalImportantNoteVisible, office.hospitalityEnabled]);

  const onHospitalityEnabled = async (val: boolean) => {
    dispatch(enableHospitalityForOffice({office: office, enabledState: val}));
  }

  const onAddPeopleToNotifyClicked = () => {
    dispatch(openDialogWithPayload({
      activeDialog: DialogIdentifiers.UserSearchDialog,
      payload: {
        onClose: () => {},
        title: 'edit-workgroup.select-users-to-add',
        description: 'edit-workgroup.add-users-to-group',
        selectedUsers: usersToNotify,
        type: UserSelectType.multiSelect,
        onUserSelect: onUserToNotifyChanged,
      },
    }));
  }

  const onImportantNoteVisibleChanged = (val: boolean) => {
    setImportantNoteVisible(val);
    dispatch(updateImportantNoteVisibility({office: office, enabledState: val}));
  }

  const onAddNewHospitalityItemClicked = () => {
    dispatch(openDialogWithPayload({
      payload: { office: office },
      activeDialog: DialogIdentifiers.HospitalityCreateOrderItemDialog
    }));
  }

  const onUserToNotifyChanged = (selectedUser: BasicUserInfo) => {
    dispatch(updateUsersToNotify({office: office, userToNotify: selectedUser}));
  }

  const onEditImportantNoteClicked = () => {
    dispatch(openDialogWithPayload({
      payload: { office: office },
      activeDialog: DialogIdentifiers.HospitalityEditImportantNote
    }));
  }

  return (
    <Container>
      {isLoading && <LoadingSpinner fullScreen={true} ignoreScroll={true} />}
      <HeadlineMedium style={{marginBottom: 24}}>{t('settings.manage-hospitality.title')}</HeadlineMedium>
      <BodyRegular>{t('settings.manage-hospitality.hospitality-description')}</BodyRegular>
      <HorizontalSpacer />
      <Switch value={hospitalityEnabled}
              weight={600}
              label={"settings.manage-hospitality.enable-hospitality"}
              onChange={onHospitalityEnabled} />
      {hospitalityEnabled && <>
        <HorizontalSpacer />
        <BodyRegular style={{marginTop: 48}} weight={600}>{t('settings.manage-hospitality.notifications')}</BodyRegular>
        <BodyRegular>{t('settings.manage-hospitality.add-people-message')}</BodyRegular>
        <BodyRegular style={{marginBottom: 16}}>{t('settings.manage-hospitality.number-of-people-notifying', {count: usersToNotify.length})}</BodyRegular>
        <OutlineButton text={'settings.manage-hospitality.add-people-to-notify'}
                       click={onAddPeopleToNotifyClicked} />

        <BodyRegular weight={600} style={{marginTop: 48}}>{t('settings.manage-hospitality.important-note-title')}</BodyRegular>
        <ImportantNoteContainer>
          <ImportantNoteRow>
            <BodySmall weight={400}>{office.hospitalityImportantNote || t('settings.manage-hospitality.important-note')}</BodySmall>
          </ImportantNoteRow>
          {!!office.hospitalityImportantNote && <Switch value={importantNoteVisible}
                                                      weight={600}
                                                      onChange={onImportantNoteVisibleChanged} />}
        </ImportantNoteContainer>
        <OutlineButton text={'Edit'} style={{marginBottom: 32}} click={onEditImportantNoteClicked} />

        <SpaceBetweenRow style={{marginBottom: 12}}>
          <BodyRegular weight={600}>{t('settings.manage-hospitality.menu-item')}</BodyRegular>
          <BodyRegular weight={600}>{t('settings.manage-hospitality.availability')}</BodyRegular>
        </SpaceBetweenRow>
        {orderItems.map((item, key) => <HospitalityMenuItem item={item} office={office} key={key} />)}

        <PrimaryButton click={onAddNewHospitalityItemClicked} style={{marginTop: 48}}
                       text={'settings.manage-hospitality.add-new-item'} />
      </>}

    </Container>
  )
}

interface Props {
  office: OfficeEntity;
}

const Container = styled.div`
`

const ImportantNoteContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  margin-top: 8px;
`

const ImportantNoteRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 80%;
  p {
    width: 100%;
    padding: 10px 16px;
    border-radius: 12px;
    margin-right: 32px;
    background-color: #E6FCFF;
  }
`
